import React from "react";
import media from "../media";
import moment from "moment";
import { Badge } from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  RedoOutlined,
} from "@ant-design/icons";

const ExportPdf = (props) => {
  const { expenseDetail, users, locations, departments, projectCodes, expenseCodes, allCurrencies } = props;  

  // -----------------------------
  // PROJECT CODE
  // -----------------------------
  const getProjectCodeLabel = (projectCodeId, projectCodes = []) => {
    if (!projectCodeId) return "N/A";

    // case: already populated from backend
    if (typeof projectCodeId === "object") {
      const { code, companyName } = projectCodeId;
      return code ? `${code} - ${companyName || ""}` : "N/A";
    }

    // case: ID
    const info = projectCodes?.find((p) => String(p.value) === String(projectCodeId));
    if (!info?.label) return "N/A";

    const [code = "", companyName = ""] = info.label.split(" - ");
    return code ? `${code} - ${companyName}` : "N/A";
  };


  // -----------------------------
  // CURRENCY
  // -----------------------------
  const getCurrencyCode = (currencyId, allCurrencies = []) => {
    if (!currencyId) return "N/A";

    // populated object
    if (typeof currencyId === "object") {
      return currencyId?.code || "N/A";
    }

    const info = allCurrencies?.find(
      (c) => String(c.id) === String(currencyId)
    );

    return info?.code || "N/A";
  };


  // -----------------------------
  // EXPENSE CODE
  // -----------------------------
  const getExpenseCodeLabel = (expenseCodeId, expenseCodes = []) => {
    if (!expenseCodeId) return "N/A";

    // populated object
    if (typeof expenseCodeId === "object") {
      const { code, name } = expenseCodeId;
      return code ? `${code} - ${name || ""}` : "N/A";
    }

    const info = expenseCodes?.find(
      (e) => String(e.value) === String(expenseCodeId)
    );

    if (!info?.label) return "N/A";

    const [code = "", name = ""] = info.label.split(" - ");
    return code ? `${code} - ${name}` : "N/A";
  };


  // -----------------------------
  // DEPARTMENT
  // -----------------------------
  const getDepartmentLabel = (departmentId, departments = []) => {
    if (!departmentId) return "N/A";

    // populated object
    if (typeof departmentId === "object") {
      const { name, code } = departmentId;
      return name ? `${name}${code ? ` - ${code}` : ""}` : "N/A";
    }

    const info = departments?.find(
      (d) => String(d.value) === String(departmentId)
    );

    if (!info?.label) return "N/A";

    return info.label;
  };

  const travelLocationLabel = React.useMemo(() => {
    if (
      expenseDetail?.natureOfExpense === "Travel Settlement" &&
      Array.isArray(expenseDetail?.travelSettlement) &&
      expenseDetail.travelSettlement.length > 0 &&
      Array.isArray(locations) &&
      locations.length > 0
    ) {
      const locationId = expenseDetail.travelSettlement[0].location;

      const matchedLocation = locations?.find(
        (loc) => String(loc.value) === String(locationId)
      );

      return matchedLocation?.label || locationId;
    }
    return null;
  }, [expenseDetail, locations]);

  const getStatusBadge = (status, reason) => {
    switch (status) {
      case "APPROVED":
        return (
          <Badge
            status="success"
            text="Approved"
            icon={<CheckCircleOutlined />}
            className="approved-badge"
          />
        );
      case "PENDING":
        return (
          <Badge
            status="warning"
            text="Pending Approval: Request Sent"
            icon={<ClockCircleOutlined />}
            className="pending-badge"
          />
        );
      case "CANCELED":
        return (
          <Badge
            status="error"
            text={
              <span>
                <strong>Canceled</strong>: {reason}
              </span>
            }
            icon={<CloseCircleOutlined />}
            className="canceled-badge"
          />
        );
      case "SENTBACK":
        return (
          <Badge
            status="processing"
            text={
              <span>
                <strong>Sent Back</strong>: {reason}
              </span>
            }
            icon={<RedoOutlined />}
            className="sentBack-badge"
          />
        );
      default:
        return (
          <Badge
            status="default"
            text="Awaiting previous Approver's Approval"
            icon={<ExclamationCircleOutlined />}
          />
        );
    }
  };
  return (
    <div className="pdf-inner">
      <div className="border-2 border-bottom border-light-light mb-6 pb-5">
        <div className="row">
          <div className="col-sm-12">
            <img src={media.PdfLogo} alt="logo" className="w-25" />
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-sm-3 mb-6">
          <label className="font-size-sm font-weight-bold text-dark-50">
            Employee ID
          </label>
          <p className="m-0 font-weight-boldest">{expenseDetail?.empID}</p>
        </div>

        <div className="col-sm-3 mb-6">
          <label className="font-size-sm font-weight-bold text-dark-50">
            Employee Name
          </label>
          <p className="m-0 font-weight-boldest">{expenseDetail?.empName}</p>
        </div>

        <div className="col-sm-3 mb-6">
          <label className="font-size-sm font-weight-bold text-dark-50">
            Department
          </label>
          <p className="m-0 font-weight-boldest">            
             {getDepartmentLabel(expenseDetail?.departmentID, departments)}
          </p>
        </div>

        <div className="col-sm-3 mb-6">
          <label className="font-size-sm font-weight-bold text-dark-50">
            Expense Period: (From - To)
          </label>
          <p className="m-0 font-weight-boldest">
            {[
              expenseDetail?.expensePeriodFromDate &&
              expenseDetail?.expensePeriodToDate
                ? moment(expenseDetail?.expensePeriodFromDate).format(
                    "DD MMM, YYYY"
                  ) +
                  " - " +
                  moment(expenseDetail?.expensePeriodToDate).format(
                    "DD MMM, YYYY"
                  )
                : null,
            ]}
          </p>
        </div>
        <div className="col-sm-3 mb-6">
          <label className="font-size-sm font-weight-bold text-dark-50">
            Project Code
          </label>
          <p className="m-0 font-weight-boldest">
            {/* {expenseDetail?.projectCodeID} */}
            {getProjectCodeLabel(expenseDetail?.projectCodeID, projectCodes)}
            {/* {expenseDetail?.projectCodeID?.code
              ? `${expenseDetail.projectCodeID?.code}-${expenseDetail.projectCodeID?.companyName}`
              : "N/A"} */}
          </p>
        </div>
        {expenseDetail?.natureOfExpense && (
          <div className="col-sm-3 mb-6">
            <label className="font-size-sm font-weight-bold text-dark-50">
              Nature of Expense
            </label>
            <p className="m-0 font-weight-boldest">
              {expenseDetail?.natureOfExpense}
            </p>
          </div>
        )}

        <div className="col-sm-3 mb-6">
          <label className="font-size-sm font-weight-bold text-dark-50">
            Vendor Name
          </label>
          <p className="m-0 font-weight-boldest">
            {expenseDetail?.businessPurpose}
          </p>
        </div>
      </div>
      {/* Travel Settlement */}
      {expenseDetail?.natureOfExpense === 'Travel Settlement' && expenseDetail?.travelSettlement &&
        <>
        <div className="border border-light-light col-sm-12 my-3"></div>
        <div className="row">
          <div className="col-sm-3 mb-6">
            <label className="font-size-sm font-weight-bold text-dark-50">
              Travel Settlement Period: (From - To)
            </label>
            <p className="m-0 font-weight-boldest">
              {[
                expenseDetail?.travelSettlement?.[0]?.fromDate &&
                expenseDetail?.travelSettlement?.[0]?.fromDate
                  ? moment(expenseDetail?.travelSettlement?.[0]?.fromDate).format(
                      "DD MMM, YYYY"
                    ) +
                    " - " +
                    moment(expenseDetail?.travelSettlement?.[0]?.toDate).format(
                      "DD MMM, YYYY"
                    )
                  : null,
              ]}
            </p>
          </div>

          <div className="col-sm-3 mb-6">
            <label className="font-size-sm font-weight-bold text-dark-50">
              Location
            </label>
            <p className="m-0 font-weight-boldest">{travelLocationLabel}</p>
          </div>

          <div className="col-sm-3 mb-6">
            <label className="font-size-sm font-weight-bold text-dark-50">
              No. of Days
            </label>
            <p className="m-0 font-weight-boldest">
              {expenseDetail?.travelSettlement?.[0]?.numberOfDays}
            </p>
          </div>

          <div className="col-sm-3 mb-6">
            <label className="font-size-sm font-weight-bold text-dark-50">
              Per Diem
            </label>
            <p className="m-0 font-weight-boldest">
              {expenseDetail?.travelSettlement?.[0]?.perDiem}
            </p>
          </div>
          <div className="col-sm-3 mb-6">
            <label className="font-size-sm font-weight-bold text-dark-50">
              Total
            </label>
            <p className="m-0 font-weight-boldest">
              {expenseDetail?.travelSettlement?.[0]?.totalForeignAmount}
            </p>
          </div>
          
          {expenseDetail?.natureOfExpense && (
            <div className="col-sm-3 mb-6">
              <label className="font-size-sm font-weight-bold text-dark-50">
                Total (INR)
              </label>
              <p className="m-0 font-weight-boldest">
                {expenseDetail?.travelSettlement?.[0]?.totalINRAmount}
              </p>
            </div>
          )}           
        
        
          <div className="col-sm-12 mb-6">
            <h4 className="font-weight-bold text-dark-50 d-block">Advance Details</h4>
          </div>
          {expenseDetail?.travelSettlement?.[0]?.advanceAmount && 
          <div className="col-sm-3 mb-6">
            <label className="font-size-sm font-weight-bold text-dark-50">
              Advance Amount
            </label>
            <p className="m-0 font-weight-boldest">
              {expenseDetail?.travelSettlement?.[0]?.advanceAmount}
            </p>
          </div>}
          {expenseDetail?.travelSettlement?.[0]?.travelSettleMediaInfo?.length !== 0 && (
            <div className="col-sm-12 mb-6">
              <label className="font-size-sm font-weight-bold text-dark-50">
                Uploaded Document
              </label>
              <div className="row">
                {expenseDetail?.travelSettlement?.[0]?.travelSettleMediaInfo?.map((travelSettleMediaItem, travelSettleMediaIndex) => {
                  return (
                    <div className="col-sm-6 mb-3" key={travelSettleMediaIndex}>
                      <img
                        className="rounded w-50"
                        key={travelSettleMediaIndex}
                        src={`${process.env.REACT_APP_BACKEND}/${travelSettleMediaItem?.savedFilepath}`}
                        alt="travelSettleMedia"
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}        
        </div>
        </>
      }
      {/* Travel Settlement Ends*/}
      
      <div className="border border-light-light col-sm-12 my-3"></div>

      {expenseDetail?.expensesDetails &&
        expenseDetail?.expensesDetails?.map((item, index) => {
          return (
            <React.Fragment key={item?._id || item?.id || index}>
              <div className="row">                
                <div className="col-sm-12 mb-6">
                  <h4 className="font-weight-bold text-dark-50 d-block">Expense Detail #{index+1}</h4>
                </div>
                <div className="col-sm-3 mb-6">
                  <label className="font-size-sm font-weight-bold text-dark-50">
                    Date
                  </label>
                  <p className="m-0 font-weight-boldest">
                    {[
                      item?.date
                        ? moment(item?.date).format(
                            "DD MMM, YYYY"
                          )
                        : null,
                    ]}
                  </p>
                </div>
                <div className="col-sm-3 mb-6">
                  <label className="font-size-sm font-weight-bold text-dark-50">
                    Description
                  </label>
                  <p className="m-0 font-weight-boldest">{item?.description}</p>
                </div>
                <div className="col-sm-3 mb-6">
                  <label className="font-size-sm font-weight-bold text-dark-50">
                    Department
                  </label>
                  <p className="m-0 font-weight-boldest">
                    {/* {item?.departmentID?.name} */}
                    {getDepartmentLabel(item?.departmentID, departments)}
                  </p>
                </div>
                <div className="col-sm-3 mb-6">
                  <label className="font-size-sm font-weight-bold text-dark-50">
                    Expense Code
                  </label>
                  <p className="m-0 font-weight-boldest">
                    {/* {item?.expenseCodeID} */}
                    {getExpenseCodeLabel(item?.expenseCodeID, expenseCodes)}
                  </p>
                </div>
                <div className="col-sm-3 mb-6">
                  <label className="font-size-sm font-weight-bold text-dark-50">
                    Currency
                  </label>
                  <p className="m-0 font-weight-boldest">
                    {/* {item?.currencyID} */}
                    {getCurrencyCode(item?.currencyID, allCurrencies)}
                  </p>
                </div>
                <div className="col-sm-3 mb-6">
                  <label className="font-size-sm font-weight-bold text-dark-50">
                    Cost
                  </label>
                  <p className="m-0 font-weight-boldest">
                    {item?.cost}
                    
                    </p>
                </div>{" "}
                {item?.mediaInfo?.length !== 0 && (
                  <div className="col-sm-12 mb-6">
                    <label className="font-size-sm font-weight-bold text-dark-50">
                      Uploaded Document
                    </label>
                    <div className="row">
                      {item?.mediaInfo?.map((mediaItem, mediaIndex) => {
                        return (
                          <div className="col-sm-6 mb-3" key={mediaIndex}>
                            <img
                              className="rounded w-50"
                              key={index}
                              src={`${process.env.REACT_APP_BACKEND}/${mediaItem?.savedFilepath}`}
                              alt="media"
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div className="border border-light-light col-sm-12 mb-3"></div>
              </div>
            </React.Fragment>
          );
        })}

      <div className="row">
        <div className="col-sm-12">
          <h4 className="font-weight-bold text-dark-50 d-block">
            Approvers Status
          </h4>

          {(() => {
            // Get unique approvers (latest per user)
            const uniqueApprovers = Object.values(
              (expenseDetail?.approvers || []).reduce((acc, curr) => {
                if (
                  !acc[curr.userID] ||
                  curr.dateTime > acc[curr.userID].dateTime
                ) {
                  acc[curr.userID] = curr;
                }
                return acc;
              }, {})
            );

            if (!uniqueApprovers.length) return null;

            return uniqueApprovers?.map((approver) => {
              // 🔹 Find user info
              const approverInfo = users?.find(
                (userItem) => userItem.id === approver?.userID
              );

              return (
                <div className="m-0 mb-4" key={approver?.userID}>
                  {approverInfo?.profilePicUrl ? (
                        <img
                          src={
                            process.env.REACT_APP_BACKEND +
                            "/images/" +
                            approverInfo.profilePicUrl
                          }
                          alt="profile"
                          className="mr-2"
                          style={{
                            width: 20,
                            height: 20,
                            borderRadius: "50%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <span className="initialLetter mr-2">
                          {approverInfo?.empName
                            ?.charAt(0)
                            ?.toUpperCase() || "?"}
                        </span>
                      )}

                  <strong>Approver Name: </strong>
                  {approverInfo?.empName ||
                    approver?.additional?.name ||
                    "N/A"}

                  <p className="my-2 font-weight-boldest">
                    {approver.status ===
                    process.env.REACT_APP_PENDING_STATUS?.toUpperCase()
                      ? "Pending Approval: Request Sent"
                      : approver.status
                      ? approver.status
                      : "Awaiting previous Approver's Approval"}
                  </p>

                  <p className="m-0 font-weight-boldest">
                    {approver?.reason || ""}
                  </p>
                </div>
              );
            });
          })()}
        </div>

        <div className="col-sm-12 mt-5">
          {expenseDetail?.adminApprover &&
          expenseDetail?.adminApprover?.length > 0 ? (
            expenseDetail.adminApprover?.map((adminApprover) => {
              // Find admin user info
              const adminApproverInfo = users?.find(
                (userItem) => userItem.id === adminApprover?.userID
              );

              return (
                <div key={adminApprover?._id || adminApprover?.userID}>
                  <h4 className="font-weight-bold text-dark-50 d-block">
                    Admin Action Status
                  </h4>

                  <div className="m-0">
                    {/* Profile / Initial */}
                    <div className="d-flex align-items-center mb-2">
                      {adminApproverInfo?.profilePicUrl ? (
                        <img
                          src={
                            process.env.REACT_APP_BACKEND +
                            "/images/" +
                            adminApproverInfo.profilePicUrl
                          }
                          alt="profile"
                          className="mr-2"
                          style={{
                            width: 20,
                            height: 20,
                            borderRadius: "50%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <span className="initialLetter mr-2">
                          {adminApproverInfo?.empName
                            ?.charAt(0)
                            ?.toUpperCase() || "?"}
                        </span>
                      )}

                      <strong>Approver Name: </strong>
                      <span className="ml-1">
                        {adminApproverInfo?.empName || "N/A"}
                      </span>

                    </div>

                    {/* Status */}
                    <p className="my-2 font-weight-boldest">
                      {adminApprover?.status || "N/A"}
                    </p>

                    {/* Reason */}
                    <p className="m-0">
                      {adminApprover?.reason || ""}
                    </p>

                  </div>
                </div>
              );
            })

          ) : (
            <>
              <div className="border border-light-light col-sm-12 mt-3 mb-3"></div>
              <h4 className="font-weight-bold text-dark-50 d-block">
                Admin Actions Status
              </h4>

              <p className="my-2">
                Admin action will take place after all approvers complete their
                approvals.
              </p>
            </>
          )}
        </div>
        {expenseDetail?.seekAcknowledgment?.[0]?.sentAt !== null &&
          (expenseDetail?.seekAcknowledgment?.[0]?.ackMessage === null ||
            expenseDetail?.seekAcknowledgment?.[0]?.ackMessage !== null) && (
            <div className="col-sm-12 mt-5">
              <h4 className="font-weight-bold text-dark-50 d-block">
                {expenseDetail?.seekAcknowledgment?.[0]?.ackMessage === null
                  ? "Seek Acknowledgement"
                  : "Acknowledgement Received"}
              </h4>

              <div className="m-0 mb-2">
                <p className="m-0 font-weight-boldest">
                  {expenseDetail?.seekAcknowledgment?.[0]?.ackMessage === null
                    ? "Request sent"
                    : expenseDetail?.seekAcknowledgment?.[0]?.ackMessage}
                </p>
              </div>
            </div>
          )}
        {/* {expenseDetail?.onBehalfApprovers && (
          <div className="col-sm-12 mt-5">
            <h4 className="font-weight-bold text-dark-50 d-block">
            Approved by Admin on behalf of following approvers:
            </h4>
            <div className="m-0 mb-2">
              <p className="m-0 font-weight-boldest">
              {expenseDetail?.onBehalfApprovers}
              </p>
            </div>
          </div>                
        )} */}
        {expenseDetail?.paymentPaidOn && (
          <div className="col-sm-12 mt-5 mb-6">
            <h4 className="font-weight-bold text-dark-50 d-block">
              Payment Status
            </h4>
            <div className="m-0 mb-2">
              <p className="m-0 font-weight-boldest">
                {" "}
                Payment Paid On:{" "}
                {moment(expenseDetail?.paymentPaidOn).format("Do MMM, YYYY")}
              </p>
            </div>
          </div>
        )}
        
        {expenseDetail?.totalReimbursement && 
          (
          <>
            <div className="border border-light-light col-sm-12 mt-3 mb-3"></div>
            <div className="col-sm-12 mb-6">
              <h4 className="font-weight-bold text-dark-50 d-block">Total Reimbursement</h4>
            </div>
            <div className="col-sm-3">            
              <p className="m-0 font-weight-boldest">
                {expenseDetail?.totalReimbursement}
              </p>
            </div>
          </>
        )}
      </div>      
  </div>
  );
};
export default ExportPdf;
